// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, LIABILITY_TYPES, getAccountTransactions } from '../../lib/eventLog.js';
import { getExchangeRate, convertCurrency } from '../../lib/currency-math.js';
import { getCurrencyDecimalPlaces } from '../../lib/currencies.js';
import { AccountForm } from './AccountForm.js';
import {
  INVESTMENT_ACCOUNT_TYPES,
  getCommodityId,
  calculateCostBasis,
  calculateTotalCostBasis,
  calculateSharesHeld,
  calculateMarketValue,
  calculateUnrealizedGain,
  getLatestPrice,
  formatShares,
} from '../../lib/investment.js';
import type { Account, AccountType, Price } from '../../types.js';

type AccountGroupKey = 'assets' | 'liabilities' | 'income' | 'expenses' | 'equity' | 'trading';

const TOP_LEVEL_GROUPS: { labelKey: AccountGroupKey; types: AccountType[] }[] = [
  { labelKey: 'assets', types: ['ASSET', 'BANK', 'CASH', 'INVESTMENT', 'RECEIVABLE', 'MUTUAL_FUND', 'STOCK', 'ACCOUNTS_RECEIVABLE'] },
  { labelKey: 'liabilities', types: ['LIABILITY', 'CREDIT', 'PAYABLE', 'CREDIT_CARD', 'ACCOUNTS_PAYABLE'] },
  { labelKey: 'income', types: ['INCOME'] },
  { labelKey: 'expenses', types: ['EXPENSE'] },
  { labelKey: 'equity', types: ['EQUITY'] },
  { labelKey: 'trading', types: ['TRADING'] },
];

function getBalanceColor(account: Account): string {
  if (account.placeholder) return 'text-text-subtle';
  if (LIABILITY_TYPES.has(account.type)) {
    return account.balance > 0 ? 'text-positive' : account.balance < 0 ? 'text-negative' : 'text-text-muted';
  }
  return account.balance < 0 ? 'text-negative' : 'text-text';
}

function getTypeLabel(type: AccountType): string {
  const labels: Record<string, string> = {
    BANK: 'BANK', CASH: 'CASH', ASSET: 'ASSET', CREDIT_CARD: 'CREDIT',
    EQUITY: 'EQUITY', EXPENSE: 'EXPENSE', INCOME: 'INCOME', LIABILITY: 'LIAB',
    MUTUAL_FUND: 'MUTUAL', STOCK: 'STOCK', ACCOUNTS_RECEIVABLE: 'A/R',
    ACCOUNTS_PAYABLE: 'A/P', ROOT: 'ROOT',
    CREDIT: 'CREDIT', INVESTMENT: 'INVEST', RECEIVABLE: 'RECV', PAYABLE: 'PAY',
  };
  return labels[type] ?? type;
}

function computeSubtotal(account: Account, allAccounts: Account[]): number {
  let total = account.balance;
  const children = allAccounts.filter((a) => a.parentId === account.id);
  for (const child of children) {
    total += computeSubtotal(child, allAccounts);
  }
  return total;
}

interface AccountRowProps {
  account: Account;
  allAccounts: Account[];
  depth: number;
  onSelect: (id: string) => void;
  onEdit: (account: Account) => void;
  showHidden: boolean;
  investmentData?: Map<string, { marketValue: number | null; unrealized: { amount: number; percentage: number } | null; sharesHeld: number }> | undefined;
  homeCurrency: string;
  allPrices: Price[];
  today: string;
}

function AccountRow({ account, allAccounts, depth, onSelect, onEdit, showHidden, investmentData, homeCurrency, allPrices, today }: AccountRowProps) {
  const { t } = useTranslation(['accounting', 'transactions', 'common', 'errors']);
  const [expanded, setExpanded] = useState(true);
  const children = allAccounts.filter((a) => a.parentId === account.id && (showHidden || !a.hidden));

  if (account.hidden && !showHidden) return null;

  const subtotal = computeSubtotal(account, allAccounts);
  const invData = investmentData?.get(account.id);
  const isInvestment = INVESTMENT_ACCOUNT_TYPES.has(account.type) && !account.placeholder;
  const displayValue = isInvestment && invData?.marketValue != null ? invData.marketValue : subtotal;

  const isForeign = account.currency !== homeCurrency && !account.placeholder;
  const homeRate = isForeign ? getExchangeRate(account.currency, homeCurrency, today, allPrices) : null;
  const homeValue = homeRate !== null
    ? convertCurrency(subtotal, account.currency, homeCurrency, homeRate, getCurrencyDecimalPlaces(account.currency), getCurrencyDecimalPlaces(homeCurrency))
    : null;

  return (
    <>
      <tr
        className={`border-b border-border group hover:bg-bg-elevated cursor-pointer transition-colors ${account.hidden ? 'opacity-50' : ''}`}
        onClick={() => {
          if (!account.placeholder) onSelect(account.id);
          if (children.length > 0) setExpanded(!expanded);
        }}
      >
        <td
          className="py-1.5 pr-4"
          style={{ paddingLeft: `${16 + depth * 16}px` }}
        >
          <div className="flex items-center gap-1">
            {children.length > 0 && (
              <span className="text-text-subtle text-xs w-3">{expanded ? '▾' : '▸'}</span>
            )}
            {children.length === 0 && <span className="w-3" />}
            <span className={account.placeholder ? 'text-text-subtle text-sm' : 'text-text text-sm'}>
              {account.name}
            </span>
            {account.hidden && <span className="text-xs text-text-subtle">{t('accountTree.hidden')}</span>}
          </div>
        </td>
        <td className="py-1.5 px-4">
          {!account.placeholder && (
            <span className="text-xs font-mono text-text-subtle">
              {getTypeLabel(account.type)}
            </span>
          )}
        </td>
        <td className={`py-1.5 px-4 text-sm text-right font-mono ${getBalanceColor(account)}`}>
          {account.placeholder ? (
            <span className="text-text-subtle text-xs">{formatAmount(subtotal, account.currency)}</span>
          ) : isInvestment && invData ? (
            <div>
              <div>{invData.marketValue != null ? formatAmount(invData.marketValue, account.currency) : t('accountTree.sharesCount', { shares: formatShares(invData.sharesHeld) })}</div>
              {invData.unrealized && (
                <div className={`text-xs ${invData.unrealized.amount >= 0 ? 'text-positive' : 'text-negative'}`}>
                  {invData.unrealized.amount >= 0 ? '+' : ''}{formatAmount(invData.unrealized.amount, account.currency)}
                  {' '}({invData.unrealized.amount >= 0 ? '+' : ''}{(invData.unrealized.percentage * 100).toFixed(1)}%)
                </div>
              )}
              {invData.marketValue === null && (
                <div className="text-xs text-text-subtle">{t('accountTree.sharesNoPrice', { shares: formatShares(invData.sharesHeld) })}</div>
              )}
            </div>
          ) : isForeign ? (
            <div>
              <div className="text-text-muted text-xs">{formatAmount(subtotal, account.currency)} {account.currency}</div>
              {homeValue !== null ? (
                <div className="text-text">{formatAmount(homeValue, homeCurrency)}</div>
              ) : (
                <div className="text-text-subtle text-xs">{t('accountTree.noRate')}</div>
              )}
            </div>
          ) : (
            formatAmount(displayValue, account.currency)
          )}
        </td>
        <td className="py-1.5 px-4 text-xs font-mono text-text-subtle">{account.currency}</td>
        <td className="py-1.5 pl-2 pr-4 text-right">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(account); }}
            className="text-xs text-text-subtle hover:text-accent px-2 py-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {t('common:actions.edit')}
          </button>
        </td>
      </tr>
      {expanded && children.map((child) => (
        <AccountRow
          key={child.id}
          account={child}
          allAccounts={allAccounts}
          depth={depth + 1}
          onSelect={onSelect}
          onEdit={onEdit}
          showHidden={showHidden}
          investmentData={investmentData}
          homeCurrency={homeCurrency}
          allPrices={allPrices}
          today={today}
        />
      ))}
    </>
  );
}

interface AccountTreeProps {
  worker: Worker | null;
  keyData: ArrayBuffer | null;
}

export function AccountTree({ worker, keyData }: AccountTreeProps) {
  const { t } = useTranslation(['accounting', 'transactions', 'common', 'errors']);
  const { state } = useApp();
  const { ledger } = state;
  const navigate = useNavigate();

  const accounts = [...ledger.accounts.values()];
  const rootIds = new Set(accounts.filter((a) => a.type === 'ROOT').map((a) => a.id));
  const homeCurrency = ledger.settings.homeCurrency;

  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [showHidden, setShowHidden] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  // Pre-compute investment data for all investment accounts
  const investmentData = useMemo(() => {
    const map = new Map<string, { marketValue: number | null; unrealized: { amount: number; percentage: number } | null; sharesHeld: number }>();
    for (const account of accounts) {
      if (!INVESTMENT_ACCOUNT_TYPES.has(account.type) || account.placeholder) continue;
      const accountTxs = getAccountTransactions(ledger, account.id);
      const sharesHeld = calculateSharesHeld(account.id, accountTxs);
      const lots = calculateCostBasis(account.id, accountTxs);
      const costBasis = calculateTotalCostBasis(lots);
      const commodityId = getCommodityId(account);
      const latestPrice = getLatestPrice(commodityId, today, allPrices);
      const marketValue = latestPrice ? calculateMarketValue(sharesHeld, latestPrice.value) : null;
      const unrealized = marketValue !== null ? calculateUnrealizedGain(marketValue, costBasis) : null;
      map.set(account.id, { marketValue, unrealized, sharesHeld });
    }
    return map;
  }, [accounts, ledger, allPrices, today]);

  const filteredAccounts = useMemo(() => {
    if (!searchQuery) return accounts;
    const q = searchQuery.toLowerCase();
    return accounts.filter(
      (a) => a.name.toLowerCase().includes(q) || a.description.toLowerCase().includes(q),
    );
  }, [accounts, searchQuery]);

  function handleSelect(id: string) {
    navigate(`/accounts/${id}`);
  }

  function handleEdit(account: Account) {
    setEditingAccount(account);
    setShowForm(true);
  }

  function handleNewAccount() {
    setEditingAccount(undefined);
    setShowForm(true);
  }

  function handleCloseForm() {
    setShowForm(false);
    setEditingAccount(undefined);
  }

  if (accounts.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center text-text-muted">
        <div className="text-center">
          <p className="text-md font-medium mb-1 text-text">{t('accountTree.noAccounts')}</p>
          <p className="text-sm text-text-muted mb-4">{t('accountTree.createFirstPrompt')}</p>
          <button
            onClick={handleNewAccount}
            className="px-4 py-2 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors"
          >
            {t('accountTree.createFirst')}
          </button>
          <div className="mt-3">
            <button
              onClick={() => navigate('/setup')}
              className="text-sm text-accent hover:text-accent-hover transition-colors"
            >
              {t('accountTree.setupStarter')}
            </button>
          </div>
        </div>
        {showForm && (
          <AccountForm worker={worker} keyData={keyData} onClose={handleCloseForm} />
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-bg-surface">
        <h2 className="text-md font-semibold text-text shrink-0">{t('common:nav.chartOfAccounts')}</h2>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('accountTree.searchPlaceholder')}
          className="flex-1 max-w-xs bg-bg border border-border text-text text-sm px-3 py-1 placeholder-text-subtle focus:border-accent focus:outline-none"
        />
        <label className="flex items-center gap-1.5 text-sm text-text-muted cursor-pointer select-none shrink-0">
          <input
            type="checkbox"
            checked={showHidden}
            onChange={(e) => setShowHidden(e.target.checked)}
            className="border-border"
          />
          {t('accountTree.showHidden')}
        </label>
        <button
          onClick={handleNewAccount}
          className="px-3 py-1 bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors shrink-0"
        >
          {t('accountForm.newAccount')}
        </button>
      </div>

      <table className="w-full text-sm border-collapse">
        <thead className="bg-bg-surface border-b border-border-strong sticky top-0 z-10">
          <tr>
            <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.account')}</th>
            <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.type')}</th>
            <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.balance')}</th>
            <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-1.5 px-4">{t('columns.currency')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {TOP_LEVEL_GROUPS.map(({ labelKey, types }) => {
            const groupAccounts = filteredAccounts.filter(
              (a) =>
                types.includes(a.type) &&
                (a.parentId === null || rootIds.has(a.parentId)),
            );
            if (groupAccounts.length === 0) return null;
            return (
              <React.Fragment key={labelKey}>
                <tr className="bg-bg-surface border-b border-border">
                  <td colSpan={5} className="py-1 px-4 text-xs font-medium text-text-subtle uppercase tracking-widest">
                    {t(`accounting:accountTypeGroups.${labelKey}`)}
                  </td>
                </tr>
                {groupAccounts.map((account) => (
                  <AccountRow
                    key={account.id}
                    account={account}
                    allAccounts={accounts}
                    depth={0}
                    onSelect={handleSelect}
                    onEdit={handleEdit}
                    showHidden={showHidden}
                    investmentData={investmentData}
                    homeCurrency={homeCurrency}
                    allPrices={allPrices}
                    today={today}
                  />
                ))}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {showForm && (
        <AccountForm
          {...(editingAccount !== undefined ? { editAccount: editingAccount } : {})}
          worker={worker}
          keyData={keyData}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
}
